/**
 * VoiceRecordingBar Component
 * ChatGPT-style dense vertical tick waveform during voice recording.
 * Ticks pump with real microphone RMS volume via noise-gated envelope follower.
 */

import type React from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './VoiceRecordingBar.css';

interface VoiceRecordingBarProps {
  isProcessing?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  /** Visual variant: 'chatbox' for light bg, 'avatar' for dark overlay */
  variant?: 'chatbox' | 'avatar';
  /** Continuously updated RMS audio level (0-1) from useGoogleSTT */
  audioLevel?: number;
}

// ── Signal processing constants ────────────────────────────────
const THRESHOLD = 0.04;
const ENVELOPE_ATTACK = 0.35;
const ENVELOPE_RELEASE = 0.08;
const SPEAKING_ONSET_MS = 120;
const SILENCE_ONSET_MS = 300;
const INTENSITY_GAIN = 5.0;
const INTENSITY_ATTACK = 0.15;
const INTENSITY_RELEASE = 0.025;

// ── Tick rendering constants ───────────────────────────────────
const BASELINE_HEIGHT = 2;
const MAX_EXTRA_HEIGHT = 20;
const TICK_WIDTH = 1.5;
const TICK_GAP = 1.5;
const TICK_COLOR = 'rgba(196, 154, 108, 0.9)'; // #c49a6c (Gold/Brass)

/** Smooth 1D noise using overlapping harmonics for spatial tick correlation */
const spatialNoise = (pos: number, t: number): number =>
  Math.abs(
    0.3 * Math.sin(pos * 11.0 + t * 1.3) +
      0.25 * Math.sin(pos * 17.0 + t * 2.1 + 0.5) +
      0.2 * Math.sin(pos * 29.0 + t * 0.9 + 1.2) +
      0.15 * Math.sin(pos * 43.0 + t * 2.7 + 2.1) +
      0.1 * Math.sin(pos * 67.0 + t * 1.7 + 3.4),
  );

/** Bell-curve falloff: edges show less activity than center */
const centerFalloff = (x: number): number => {
  const d = (x - 0.5) * 2;
  return 0.3 + 0.7 * Math.exp(-3 * d * d);
};

/** ChatGPT-style dense tick waveform driven by real microphone volume */
export const VoiceRecordingBar: React.FC<VoiceRecordingBarProps> = ({
  isProcessing = false,
  onCancel,
  onConfirm,
  variant = 'chatbox',
  audioLevel = 0,
}) => {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number>(0);
  const audioLevelRef = useRef(0);

  // Signal processing state (refs so rAF loop avoids React re-renders)
  const levelSmoothRef = useRef(0);
  const isSpeakingRef = useRef(false);
  const aboveThresholdMsRef = useRef(0);
  const belowThresholdMsRef = useRef(0);
  const intensityRef = useRef(0);

  useEffect(() => {
    audioLevelRef.current = audioLevel;
  }, [audioLevel]);

  /** Process signal and render ticks at 60 fps */
  const draw = useCallback((canvas: HTMLCanvasElement) => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let lastTime = 0;

    const render = (now: number) => {
      const dt = lastTime ? (now - lastTime) / 1000 : 0.016;
      lastTime = now;
      const elapsed = now / 1000;

      const dpr = window.devicePixelRatio ?? 1;
      const w = canvas.width / dpr;
      const h = canvas.height / dpr;
      const midY = h / 2;

      // ── Signal processing ──────────────────────────────────
      const levelRaw = audioLevelRef.current;

      // Envelope follower
      const prevSmooth = levelSmoothRef.current;
      const envAlpha = levelRaw > prevSmooth ? ENVELOPE_ATTACK : ENVELOPE_RELEASE;
      const levelSmooth = prevSmooth + envAlpha * (levelRaw - prevSmooth);
      levelSmoothRef.current = levelSmooth;

      // Noise gate with timing
      if (levelSmooth > THRESHOLD) {
        aboveThresholdMsRef.current += dt * 1000;
        belowThresholdMsRef.current = 0;
        if (aboveThresholdMsRef.current >= SPEAKING_ONSET_MS) {
          isSpeakingRef.current = true;
        }
      } else {
        belowThresholdMsRef.current += dt * 1000;
        aboveThresholdMsRef.current = 0;
        if (belowThresholdMsRef.current >= SILENCE_ONSET_MS) {
          isSpeakingRef.current = false;
        }
      }

      // Compute target intensity
      let targetIntensity = 0;
      if (isSpeakingRef.current) {
        targetIntensity = Math.min(1, Math.max(0, (levelSmooth - THRESHOLD) * INTENSITY_GAIN));
      }

      // Smooth intensity (fast attack, slow release for ~0.5-1s decay)
      const prevIntensity = intensityRef.current;
      const intAlpha = targetIntensity > prevIntensity ? INTENSITY_ATTACK : INTENSITY_RELEASE;
      intensityRef.current = prevIntensity + intAlpha * (targetIntensity - prevIntensity);
      const intensity = intensityRef.current;

      // ── Rendering ──────────────────────────────────────────
      ctx.save();
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);

      const tickStep = TICK_WIDTH + TICK_GAP;
      const tickCount = Math.min(220, Math.max(140, Math.floor(w / tickStep)));
      const totalWidth = tickCount * tickStep;
      const startX = (w - totalWidth) / 2;

      // Batch all ticks in one stroke call for performance
      ctx.strokeStyle = TICK_COLOR;
      ctx.lineWidth = TICK_WIDTH;
      ctx.lineCap = 'round';
      ctx.shadowColor = 'rgba(200, 210, 230, 0.25)';
      ctx.shadowBlur = 2;
      ctx.beginPath();

      for (let i = 0; i < tickCount; i++) {
        const x = startX + i * tickStep + TICK_WIDTH / 2;
        const pos = (i / tickCount) * 20;
        const noise = spatialNoise(pos, elapsed);
        const falloff = centerFalloff(i / tickCount);
        const tickH = BASELINE_HEIGHT + noise * falloff * intensity * MAX_EXTRA_HEIGHT;

        ctx.moveTo(x, midY - tickH / 2);
        ctx.lineTo(x, midY + tickH / 2);
      }

      ctx.stroke();
      ctx.restore();

      rafRef.current = requestAnimationFrame(render);
    };

    rafRef.current = requestAnimationFrame(render);
  }, []);

  /** Set up canvas sizing + start rendering */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio ?? 1;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
    };

    resize();
    window.addEventListener('resize', resize);
    draw(canvas);

    return () => {
      window.removeEventListener('resize', resize);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = 0;
      }
    };
  }, [draw]);

  return (
    <div className={`voice-recording-bar voice-recording-bar--${variant}`}>
      <div className="voice-recording-bar__waveform" aria-hidden="true">
        <canvas ref={canvasRef} className="voice-recording-bar__canvas" />
      </div>

      <div className="voice-recording-bar__actions">
        {isProcessing ? (
          <output
            className="voice-recording-bar__spinner"
            aria-label={t('chat.processing', 'Processing...')}
          />
        ) : (
          <>
            <button
              type="button"
              className="voice-recording-bar__btn voice-recording-bar__btn--cancel"
              onClick={onCancel}
              aria-label={t('chat.cancelRecording', 'Cancel recording')}
              title={t('chat.cancelRecording', 'Cancel recording')}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <title>Cancel</title>
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
            <button
              type="button"
              className="voice-recording-bar__btn voice-recording-bar__btn--confirm"
              onClick={onConfirm}
              aria-label={t('chat.confirmRecording', 'Send recording')}
              title={t('chat.confirmRecording', 'Send recording')}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <title>Confirm</title>
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </button>
          </>
        )}
      </div>
    </div>
  );
};
